import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import SearchIcon from "@mui/icons-material/Search";
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress,
  Grid,
  TextField,
} from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useLoaderData } from "react-router-dom";
import { toast } from "react-toastify";

import DefaultApiService from "../../../../infrastructure/api/default";
import useGetPageInfo from "../../../common/hooks/useGetPageInfo";
import BreadcrumbsComponent from "../../../components/shared/breadcrumbs";
import DropdownPicker from "../../../components/shared/dropdown";
import JalaliDatePicker from "../../../components/shared/jalali-datepicker";
import EnhancedTable from "../../../components/shared/table";
import { toMiladyFullDate } from "../../../middleware/middleware";

const todayMilady = () => toMiladyFullDate(new Date());

const UserVerificationCodesReportPage = () => {
  const pageInfo = useGetPageInfo();
  const filterData = useLoaderData("root") || {};

  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState({
    total: 0,
    verified: 0,
    pending: 0,
    expired: 0,
  });
  const [filterValues, setFilterValues] = useState({
    StartDate: todayMilady(),
    EndDate: todayMilady(),
    MobileNumber: "",
    StatusFilter: "All",
  });

  const statusFilters = useMemo(() => {
    const list = filterData?.statusFilters || filterData?.StatusFilters || [];
    return list.map((item) => ({
      id: item.id ?? item.Id,
      title: item.title ?? item.Title,
    }));
  }, [filterData]);

  const loadReport = useCallback(async () => {
    try {
      setLoading(true);
      const res = await DefaultApiService.postApi(
        "UM/Yeksa/UserVerificationCodesReport/GetData",
        filterValues
      );

      const payload = res?.data ?? res?.Data ?? {};
      const items = payload?.items ?? payload?.Items ?? [];
      const sum = payload?.summary ?? payload?.Summary ?? {};

      setRows(
        items.map((row) => ({
          ...row,
          id: row.id ?? row.Id,
          mobileNumber: row.mobileNumber ?? row.MobileNumber,
          verifyCode: row.verifyCode ?? row.VerifyCode,
          createDate: row.createDate ?? row.CreateDate,
          expireDate: row.expireDate ?? row.ExpireDate,
          statusTitle: row.statusTitle ?? row.StatusTitle,
          outcomeTitle: row.outcomeTitle ?? row.OutcomeTitle,
          validityTitle: row.validityTitle ?? row.ValidityTitle,
          isCurrentlyValid: row.isCurrentlyValid ?? row.IsCurrentlyValid,
        }))
      );
      setSummary({
        total: sum.total ?? sum.Total ?? 0,
        verified: sum.verified ?? sum.Verified ?? 0,
        pending: sum.pending ?? sum.Pending ?? 0,
        expired: sum.expired ?? sum.Expired ?? 0,
      });
    } catch (error) {
      toast.error(error?.response?.data?.message || "خطا در دریافت گزارش");
      setRows([]);
      setSummary({ total: 0, verified: 0, pending: 0, expired: 0 });
    } finally {
      setLoading(false);
    }
  }, [filterValues]);

  useEffect(() => {
    loadReport();
  }, []);

  const onClearFilters = () => {
    setFilterValues({
      StartDate: todayMilady(),
      EndDate: todayMilady(),
      MobileNumber: "",
      StatusFilter: "All",
    });
  };

  const headCells = [
    { id: "createDate", type: "persiandatetime", label: "زمان ارسال", disablePadding: true },
    { id: "mobileNumber", label: "موبایل" },
    { id: "verifyCode", label: "کد OTP" },
    { id: "expireDate", type: "persiandatetime", label: "انقضا" },
    { id: "outcomeTitle", label: "نتیجه" },
    { id: "validityTitle", label: "اعتبار" },
    { id: "statusTitle", label: "وضعیت سیستم" },
  ];

  return (
    <Grid container spacing={1}>
      <Grid item xs={12}>
        <BreadcrumbsComponent />
      </Grid>

      <Grid item xs={12}>
        <Card>
          <CardHeader
            title={pageInfo?.title || "گزارش کدهای ورود OTP"}
            subheader="لیست کدهای ارسال‌شده — پیش‌فرض روز جاری، جدیدترین در بالا"
            action={
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                <Button
                  variant="outlined"
                  color="primary"
                  startIcon={loading ? <CircularProgress size={16} /> : <SearchIcon />}
                  onClick={loadReport}
                  disabled={loading}
                >
                  جستجو
                </Button>
                <Button
                  variant="text"
                  color="inherit"
                  startIcon={<HighlightOffIcon />}
                  onClick={onClearFilters}
                >
                  بازنشانی
                </Button>
              </Box>
            }
          />
          <CardContent>
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={12} sm={6} md={3}>
                <JalaliDatePicker
                  label="از تاریخ"
                  fullWidth
                  size="small"
                  value={filterValues.StartDate}
                  onChange={(value) =>
                    setFilterValues((old) => ({ ...old, StartDate: value }))
                  }
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <JalaliDatePicker
                  label="تا تاریخ"
                  fullWidth
                  size="small"
                  value={filterValues.EndDate}
                  onChange={(value) =>
                    setFilterValues((old) => ({ ...old, EndDate: value }))
                  }
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <DropdownPicker
                  label="وضعیت"
                  size="small"
                  fullWidth
                  value={filterValues.StatusFilter}
                  list={statusFilters}
                  optionName="title"
                  optionValue="id"
                  onChange={(event) =>
                    setFilterValues((old) => ({
                      ...old,
                      StatusFilter: event.target.value,
                    }))
                  }
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  label="موبایل (اختیاری)"
                  size="small"
                  fullWidth
                  value={filterValues.MobileNumber}
                  onChange={(e) =>
                    setFilterValues((old) => ({
                      ...old,
                      MobileNumber: e.target.value,
                    }))
                  }
                />
              </Grid>
            </Grid>

            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 2 }}>
              <Chip label={`کل: ${summary.total}`} />
              <Chip label={`موفق: ${summary.verified}`} color="success" variant="outlined" />
              <Chip label={`در انتظار: ${summary.pending}`} color="info" variant="outlined" />
              <Chip label={`ناموفق/منقضی: ${summary.expired}`} color="warning" variant="outlined" />
            </Box>

            <EnhancedTable
              rows={rows}
              headCells={headCells}
              loading={loading}
              pageSize={25}
              defaultSort={{ orderBy: "createDate", order: "desc" }}
            />
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
};

export default UserVerificationCodesReportPage;
